/**
 * Phase 440 Plan 04 — RUNTIME-04 Realtime invalidator (Branch A)
 * ============================================================================
 * Bridges Phase 0440's (tenant_id, app_id, branch) cache invalidation contract
 * to the existing Phase 0439 Plan 04 Realtime subscriber on
 * `cogo.application_ontology`.
 *
 * Branch decision (per CONTEXT.md D-F2 + branch_a_binding_authoritative
 * dispatch context):
 *   - Phase 439 REALTIME-01 SHIPPED at cogo-platform commit `8a4482c1`
 *     (`feat(0439-04-platform): Realtime publication on application_ontology
 *     (REALTIME-01)`).
 *   - Phase 439 already exports `subscribeOntologyChanges` at
 *     `cogo-web/packages/cogo-kg-browser/src/realtime-subscriber.ts` with the
 *     canonical channel name `ontology:${tenantId}:${appId}` and the
 *     server-side filter `tenant_id=eq.${tenantId}` plus a client-side
 *     secondary `app_id` filter.
 *   - Plan 04 = **Branch A**. NO stub interface. NO D-440-W2-DEFERRED marker.
 *
 * Adaptation strategy:
 *   - Plan 04 requires the public surface name `subscribeToTenantOntology` and
 *     the type `IRealtimeInvalidator`. These are NEW names introduced by
 *     Phase 440 RUNTIME-04 — Phase 439 does not export them.
 *   - We therefore implement `subscribeToTenantOntology` as a thin wrapper
 *     that delegates to Phase 439's `subscribeOntologyChanges`. The wrapper:
 *       - Accepts a caller-supplied `supabaseClient` (preferred) OR builds
 *         one lazily from `supabaseUrl` + `anonKey` (per Plan 04 PLAN
 *         interface spec).
 *       - Maps Plan 04's `onInvalidate(payload)` callback to Phase 439's
 *         `onChange(OntologyChangePayload)`.
 *     This keeps a single channel-subscription path (Phase 439's) and avoids
 *     duplicating the channel/filter/event-type wiring.
 *
 * Public surface (Plan 04 PLAN.md interfaces block):
 *   - `RealtimeInvalidatorOpts` — caller options.
 *   - `Subscription`           — { unsubscribe }.
 *   - `IRealtimeInvalidator`   — interface contract for callers that want to
 *                                inject a custom invalidator (e.g. test
 *                                doubles, or a future server-side fanout
 *                                replacement).
 *   - `NOOP_REALTIME_INVALIDATOR` — default injection target; subscribe is
 *                                   a no-op that returns a Subscription
 *                                   with an idempotent unsubscribe.
 *   - `subscribeToTenantOntology(opts)` — primary entry point. Branch A real
 *                                         implementation.
 *
 * Channel contract (Phase 439 REALTIME-01, mirrored here for audit):
 *   - Channel name: `ontology:${tenantId}:${appId}`
 *   - postgres_changes filter: `tenant_id=eq.<uuid>` (server-side)
 *   - Secondary filter: `app_id` (client-side, applied inside Phase 439's
 *     handler before invoking `onChange`)
 *   - Unsubscribe: `() => supabaseClient.removeChannel(channel)`
 *
 * Threat model (Plan 04 frontmatter §threat_model):
 *   - T-440-04-01 (Spoofing) — channel name + filter are server-defined; the
 *     wrapper inherits Phase 439's RLS-gated subscription.
 *   - T-440-04-02 (Information Disclosure) — RLS at Supabase Realtime layer
 *     means cross-tenant subscription returns empty stream.
 *   - T-440-04-03 (DoS) — wrapper returns { unsubscribe } and callers MUST
 *     clean up. Lazy supabase client created here is closed via
 *     `supabaseClient.removeChannel(channel)`; the underlying socket is
 *     reference-counted by supabase-js.
 *
 * Plan 04 must_haves cross-references:
 *   - Truth #1: `subscribeToTenantOntology` exported here. ✓
 *   - Truth #4: `_broadcastChannelForTest` stays as null stub in
 *     `shard-cache.ts` (separate file, separate edit per Task 2). N/A here.
 *   - Truth #6: `LocalWasmRepository.subscribeToTenantInvalidation` consumes
 *     this module via `@cogo/kg-browser` barrel (Task 4).
 *   - Truth #7: Branch A executes (this module is the concrete binding).
 * ============================================================================
 */

import type { SupabaseClient } from "@supabase/supabase-js"
import { createClient } from "@supabase/supabase-js"

import {
  subscribeOntologyChanges,
  type OntologyChangePayload,
  type Unsubscribe,
} from "./realtime-subscriber"

// ---------------------------------------------------------------------------
// Public surface
// ---------------------------------------------------------------------------

/**
 * Plan 04 PLAN.md interfaces block — RealtimeInvalidatorOpts.
 *
 * Two equivalent ways to provide auth:
 *   (a) Pass an existing `supabaseClient` (preferred when the host app
 *       already has one initialized — avoids duplicate WebSocket sockets).
 *   (b) Pass `supabaseUrl` + `anonKey` and let the wrapper build a fresh
 *       client. Useful for cogo-kg-repository / cogo-kg-worker callers that
 *       only know the project URL + anon key.
 *
 * If both are passed, `supabaseClient` wins.
 */
export interface RealtimeInvalidatorOpts {
  /** Existing Supabase client. Preferred path. */
  readonly supabaseClient?: SupabaseClient
  /** Project URL — required if `supabaseClient` not supplied. */
  readonly supabaseUrl?: string
  /** Anon (or authenticated user) JWT — required if `supabaseClient` not supplied. */
  readonly anonKey?: string
  readonly tenantId: string
  readonly appId: string
  readonly branch?: string
  /**
   * Invoked on every postgres_changes event for the matching (tenantId, appId).
   * Payload mirrors Phase 439's `OntologyChangePayload` minus the redundant
   * tenantId/appId fields (those are pinned by the subscription itself);
   * callers usually only need `eventType` to distinguish INSERT/UPDATE/DELETE.
   */
  readonly onInvalidate: (payload: {
    eventType: "INSERT" | "UPDATE" | "DELETE"
    subject: string
    branch: string
    versionSeq?: number
    receivedAt: number
  }) => void
}

export interface Subscription {
  readonly unsubscribe: () => void
}

/**
 * Interface contract for invalidators. Production binds this to
 * `subscribeToTenantOntology` (Branch A); test doubles can implement
 * `subscribe` directly to deliver synthetic events.
 */
export interface IRealtimeInvalidator {
  subscribe(opts: RealtimeInvalidatorOpts): Subscription
}

/**
 * No-op invalidator. Returns a Subscription whose `unsubscribe` is a no-op.
 * Useful as a default value for callers that don't (yet) want live
 * invalidation — for example, SSR contexts or test fixtures.
 */
export const NOOP_REALTIME_INVALIDATOR: IRealtimeInvalidator = {
  subscribe: (_opts: RealtimeInvalidatorOpts): Subscription => ({
    unsubscribe: () => {
      /* no-op */
    },
  }),
}

/**
 * Phase 440 Plan 04 RUNTIME-04 Branch A — subscribe to per-tenant ontology
 * change events via Phase 439's Realtime subscription on
 * `cogo.application_ontology`.
 *
 * Returns a Subscription with `unsubscribe()` that the caller MUST invoke on
 * teardown to avoid leaking WebSocket connections (threat T-440-04-03).
 *
 * @example
 * ```typescript
 * const sub = subscribeToTenantOntology({
 *   supabaseUrl: "https://cjvgmyotqxfpxpvmwxfv.supabase.co",
 *   anonKey: "<anon-jwt>",
 *   tenantId: "00000000-0000-0000-0000-000000000000",
 *   appId: "platform-seed",
 *   branch: "main",
 *   onInvalidate: (evt) => {
 *     console.log(`ontology changed: ${evt.eventType} ${evt.subject}`)
 *     // ... evict cache key for (tenantId, appId, branch) ...
 *   },
 * })
 *
 * // later, on teardown:
 * sub.unsubscribe()
 * ```
 */
export function subscribeToTenantOntology(
  opts: RealtimeInvalidatorOpts,
): Subscription {
  const supabaseClient = resolveSupabaseClient(opts)

  // Adapt Plan 04's onInvalidate(payload) to Phase 439's onChange(OntologyChangePayload).
  const onChange = (event: OntologyChangePayload): void => {
    // Branch / app filtering: Phase 439's subscriber already applies a
    // server-side `tenant_id=eq` filter + a client-side `app_id` secondary
    // filter. We add an OPTIONAL client-side `branch` filter here when the
    // caller passes opts.branch — this preserves Plan 04 D-B3 cache-key
    // granularity (`${tenantId}:${appId}:${branch}`) for the case where the
    // same (tenant, app) tuple has multiple active branches and the caller
    // only wants events for one of them.
    if (opts.branch !== undefined && event.branch !== opts.branch) {
      return
    }

    opts.onInvalidate({
      eventType: event.changeKind,
      subject: event.subject,
      branch: event.branch,
      versionSeq: event.versionSeq,
      receivedAt: event.receivedAt,
    })
  }

  const unsubscribe: Unsubscribe = subscribeOntologyChanges({
    tenantId: opts.tenantId,
    appId: opts.appId,
    supabaseClient,
    onChange,
  })

  return {
    unsubscribe: () => {
      unsubscribe()
    },
  }
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function resolveSupabaseClient(opts: RealtimeInvalidatorOpts): SupabaseClient {
  if (opts.supabaseClient) return opts.supabaseClient
  if (!opts.supabaseUrl || !opts.anonKey) {
    throw new Error(
      "[realtime-invalidator] subscribeToTenantOntology requires either " +
        "{supabaseClient} or both {supabaseUrl, anonKey}",
    )
  }
  return createClient(opts.supabaseUrl, opts.anonKey)
}
